// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for EventSpeakers entity
 */
type EventSpeakersConfig = BaseMetricConfig;

/**
 * EventSpeakers Entity - Encapsulates all event speakers-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the event speakers ecosystem metric.
 */
export class EventSpeakers extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'event_speakers' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: EventSpeakersConfig = {
    metricType: 'event_speakers',
    display: {
      displayName: 'Event Speakers',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of event and event breakout session speakers are employed by our company?',
        scoringRules: [
          'Each 0.1% of event speakers for the foundation total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 event speakers = 0 points',
          '0.000001 - 0.1% of event speakers = 1.1 point',
          '0.2% of event speakers = 1.2 points',
          '2.5% of event speakers = 3.5 points',
          '20.0% of event speakers = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no event speakers for {context}.`,
      },
      positiveValue: {
        descriptionTemplate: `Employees from our company represented {percentage}% of all speakers at events for {context}.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for event speakers
   */
  static getBottomSummary(percentage: string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project\'s foundation';

    if (numericValue === 0) {
      const description = this.formatDescription(
        this.CONFIG.bottomSummary.zeroValue.description,
        { context: contextText }
      );

      return {
        title: this.CONFIG.display.displayName,
        description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      this.CONFIG.bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: percentageValue,
        context: contextText
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
